import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./layout";
import { Button } from "./ui";
import { ContextBar } from "./context-bar";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "./states";
import { apiConfigured, ApiError } from "../lib/api";
import { useSession } from "../hooks/useSession";
import { useAppContext } from "../hooks/useAppContext";

export function toMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

/**
 * Shared Phase 7 page shell: session gate + workspace (tenant) selection. Every
 * integration surface is tenant-scoped, so we only require a workspace here; the
 * target guild is part of each integration's own configuration.
 */
export function IntegrationPage({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: ReactNode;
  children: (tenantId: string) => ReactNode;
}): JSX.Element {
  const { state } = useSession();
  const ctx = useAppContext();

  if (state.kind === "loading") return <LoadingState label="Checking your session…" />;
  if (state.kind === "not-configured")
    return (
      <NotConfiguredState title="Control API URL not configured">
        Set <code>VITE_CONTROL_API_URL</code> to manage integrations.
      </NotConfiguredState>
    );
  if (state.kind === "unavailable") return <ApiUnavailableState />;
  if (state.kind === "anonymous")
    return (
      <>
        <PageHeader title={title} />
        <EmptyState title="Please sign in to continue">
          <Link to="/login">
            <Button variant="primary">Sign in with Discord</Button>
          </Link>
        </EmptyState>
      </>
    );

  return (
    <>
      <PageHeader title={title}>{intro}</PageHeader>
      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to manage integrations.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar />
          {!ctx.tenantId ? (
            <EmptyState title="Select a workspace">Choose a workspace above to manage integrations.</EmptyState>
          ) : (
            children(ctx.tenantId)
          )}
        </>
      )}
    </>
  );
}
